import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../home/components/Navbar';
import Footer from '../../home/components/Footer';
import SEOHead from '../../../components/feature/SEOHead';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

const officeSchema = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Office Cleaning Services',
  description: 'Professional office cleaning services in Louisville, KY including desk sanitization, restroom cleaning, floor care, and waste management.',
  provider: {
    '@type': 'LocalBusiness',
    name: 'First Insight Cleaning',
    url: siteUrl,
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Louisville',
      addressRegion: 'KY',
      addressCountry: 'US',
    },
  },
  areaServed: { '@type': 'City', name: 'Louisville' },
  serviceType: 'Office Cleaning',
};

export default function OfficeCleaningPage() {
  const [scrolled, setScrolled] = useState(true);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const features = [
    {
      icon: 'ri-computer-line',
      title: 'Desk & Workstation Cleaning',
      description: 'Thorough cleaning and sanitization of desks, keyboards, monitors, and office equipment to maintain a hygienic workspace.'
    },
    {
      icon: 'ri-door-line',
      title: 'Common Area Maintenance',
      description: 'Regular cleaning of lobbies, hallways, break rooms, and conference rooms to ensure a professional environment.'
    },
    {
      icon: 'ri-delete-bin-line',
      title: 'Waste Management',
      description: 'Daily trash removal, recycling services, and proper disposal of office waste materials.'
    },
    {
      icon: 'ri-water-flash-line',
      title: 'Restroom Sanitization',
      description: 'Deep cleaning and disinfection of restrooms including fixtures, floors, and surfaces with medical-grade products.'
    },
    {
      icon: 'ri-restaurant-line',
      title: 'Kitchen & Break Room',
      description: 'Complete cleaning of kitchen appliances, countertops, sinks, and dining areas to maintain food safety standards.'
    },
    {
      icon: 'ri-layout-grid-line',
      title: 'Floor Care Services',
      description: 'Vacuuming carpets, mopping hard floors, and periodic deep cleaning to preserve flooring quality and appearance.'
    }
  ];

  const benefits = [
    'Customizable cleaning schedules (daily, weekly, or bi-weekly)',
    'Eco-friendly cleaning products safe for office environments',
    'Trained and background-checked cleaning professionals',
    'Flexible service hours to minimize business disruption',
    'Quality inspections to ensure consistent standards',
    'Dedicated account manager for your office needs'
  ];

  return (
    <div className="min-h-screen bg-white font-['Inter']">
      <SEOHead
        title="Office Cleaning Services Louisville KY | First Insight Cleaning"
        description="Professional office cleaning services in Louisville, KY. Desk sanitization, restroom disinfection, floor care, waste management &amp; more. Eco-friendly products, flexible scheduling. Get a free quote today."
        keywords="office cleaning Louisville KY, commercial office janitorial, workplace cleaning Kentucky, First Insight Cleaning"
        canonical={`${siteUrl}/services/office-cleaning`}
        schemaJson={officeSchema}
      />
      <Navbar scrolled={scrolled} />
      
      {/* Hero Section */}
      <section className="relative h-[500px] flex items-center justify-center overflow-hidden mt-20">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/edited_1abb203589e926e308fed1c352496342_84f95394.jpg"
            alt="Professional Office Cleaning Services"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-900/70 via-slate-900/60 to-slate-900/70"></div>
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-6 font-['Poppins']">
            Office Cleaning Services
          </h1>
          <p className="text-base md:text-xl text-slate-100 mb-8 leading-relaxed">
            Create a productive and healthy work environment with our comprehensive office cleaning solutions. Professional service tailored to your business schedule.
          </p>
          <a
            href="/#quote-form"
            className="inline-block bg-teal-500 text-white px-8 py-4 rounded-lg font-semibold hover:bg-teal-600 transition-colors duration-300 whitespace-nowrap cursor-pointer"
          >
            Get Free Quote
          </a>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {/* Introduction */}
          <div className="mb-16">
            <h2 className="text-4xl font-bold text-slate-800 mb-6 font-['Poppins']">
              Professional Office Cleaning Solutions
            </h2>
            <div className="w-16 h-1 bg-teal-500 mb-8"></div>
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                  A clean office is essential for employee productivity, health, and creating a positive impression on clients and visitors. Our office cleaning services are designed to maintain the highest standards of cleanliness while working around your business operations.
                </p>
                <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                  We understand that every office has unique needs. Whether you operate a small startup or a large corporate facility, our team develops customized cleaning plans that address your specific requirements, budget, and schedule preferences.
                </p>
                <p className="text-lg text-slate-600 leading-relaxed">
                  Our experienced cleaning professionals use industry-leading techniques and eco-friendly products to deliver exceptional results. We focus on high-touch surfaces, common areas, and workspaces to create a healthier environment that reduces sick days and boosts employee morale.
                </p>
              </div>
              <div className="w-full h-[400px]">
                <img
                  src="https://public.readdy.ai/ai/img_res/edited_9b9b0732839c1ceb1e8a892ee20dae2b_071a3871.jpg"
                  alt="Office Cleaning in Action"
                  className="w-full h-full object-cover object-center rounded-2xl shadow-lg"
                />
              </div>
            </div>
          </div>

          {/* Features Grid */}
          <div className="mb-16">
            <h3 className="text-3xl font-bold text-slate-800 mb-12 text-center font-['Poppins']">
              Our Office Cleaning Services Include
            </h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="bg-slate-50 rounded-xl p-6 hover:shadow-lg transition-all duration-300 border border-slate-100"
                >
                  <div className="w-14 h-14 flex items-center justify-center bg-teal-500 rounded-lg mb-4">
                    <i className={`${feature.icon} text-2xl text-white`}></i>
                  </div>
                  <h4 className="text-xl font-bold text-slate-800 mb-3">
                    {feature.title}
                  </h4>
                  <p className="text-slate-600 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Benefits Section */}
          <div className="bg-teal-50 rounded-2xl p-12">
            <h3 className="text-3xl font-bold text-slate-800 mb-8 text-center font-['Poppins']">
              Why Choose Our Office Cleaning Service
            </h3>
            <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-start gap-3">
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-checkbox-circle-fill text-2xl text-teal-500"></i>
                  </div>
                  <p className="text-slate-700 text-lg">{benefit}</p>
                </div>
              ))}
            </div>
          </div>

          {/* CTA Section */}
          <div className="mt-16 text-center">
            <h3 className="text-3xl font-bold text-slate-800 mb-6 font-['Poppins']">
              Ready to Transform Your Office Environment?
            </h3>
            <p className="text-lg text-slate-600 mb-8 max-w-2xl mx-auto">
              Contact us today for a free consultation and customized cleaning plan tailored to your office needs.
            </p>
            <a
              href="/#quote-form"
              className="inline-block bg-teal-500 text-white px-10 py-4 rounded-lg font-semibold hover:bg-teal-600 transition-colors duration-300 whitespace-nowrap cursor-pointer text-lg"
            >
              Request Free Quote
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
